import { YARD_STATE_LABELS } from "@etudescarre/constants";
import { Button, cn } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { ArrowLeftIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { type YardState, yardStateBadgeClass } from "../../staff/yard-state";

export type YardScreenHeaderProps = {
  /** Yard name, shown as the main title. Null while still loading. */
  yardName: string | null;
  /** Derived yard state, shown as a badge next to the name. */
  yardState: YardState | null;
  /** Name of the back-office screen, shown under the yard name. */
  screenTitle: string;
  onBack: () => void;
  backLabel: string;
  /** Screen-level actions, right-aligned on the same line. */
  actions?: ReactNode;
};

/**
 * Shared header of the yard back-office screens (903, 904, 905, 909, 910):
 * back button, yard name with its state badge, then the screen name. It is
 * `shrink-0` so it stays fixed above the scrolling table content.
 */
export function YardScreenHeader({
  yardName,
  yardState,
  screenTitle,
  onBack,
  backLabel,
  actions,
}: YardScreenHeaderProps) {
  return (
    <div className="mb-4 flex shrink-0 items-center gap-4">
      <Button plain onClick={onBack} aria-label={backLabel} title={backLabel}>
        <ArrowLeftIcon className="size-5" />
      </Button>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2
            className="truncate text-2xl font-semibold text-gray-900 dark:text-gray-100"
            title={yardName ?? undefined}
          >
            {yardName}
          </h2>
          {isNotNil(yardState) && (
            <span
              className={cn(
                "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium",
                yardStateBadgeClass[yardState],
              )}
            >
              {YARD_STATE_LABELS[yardState]}
            </span>
          )}
        </div>
        <p className="text-sm text-zinc-500">{screenTitle}</p>
      </div>
      {actions}
    </div>
  );
}

export type AnnuaireLinkProps = { className?: string };

/**
 * Link to the staff directory (902), for the contract screens (909, 910)
 * where AdminNav is hidden. A new tab keeps the screen and its unsaved draft.
 */
export function AnnuaireLink({ className }: AnnuaireLinkProps) {
  return (
    <Button
      outline
      href="/staff/annuaire"
      target="_blank"
      rel="noopener"
      className={cn("shrink-0", className)}
    >
      <ArrowSquareOutIcon data-slot="icon" className="size-4" />
      Annuaire sociétés et collaborateurs
    </Button>
  );
}
